<template>
    <div
        ref="rangePresets"
        class="flex flex-col gap-1 h-fit bg-base-100 p-2 mt-1 rounded-none border-1 border-base-300"
    >
        <div class="range-group">
            <div v-if="!hideYearPicker" class="year-picker join grow">
                <div class="join-item range-btn" @click="selectPrevYear">
                    <i class="i-ph-caret-left"/>
                </div>
                <button
                    class="join-item range-btn font-medium grow border-b-2! range-preset-btn"
                    :class="{'range-preset-btn-active': selectedYear() === (startYear - 2)}"
                    :value="startYear - 2"
                    @click="handleClick('YEAR', () => selectYear(startYear - 2))"
                >
                    <span :class="{'border-b-2 border-b-primary': rangeIsWithinYear(startYear - 2)}">
                        {{ startYear - 2 }}
                    </span>
                </button>
                <button
                    class="join-item range-btn font-medium grow range-preset-btn"
                    :class="{'range-preset-btn-active': selectedYear() === (startYear - 1)}"
                    :value="startYear - 1"
                    @click="handleClick('YEAR', () => selectYear(startYear - 1))"
                >
                    <span :class="{'border-b-2 border-b-primary': rangeIsWithinYear(startYear - 1)}">
                        {{ startYear - 1 }}
                    </span>
                </button>
                <button
                    class="join-item range-btn font-medium grow range-preset-btn"
                    :class="{'range-preset-btn-active': selectedYear() === startYear}"
                    :value="startYear"
                    @click="handleClick('YEAR', () => selectYear(startYear))"
                >
                    <span :class="{'border-b-2 border-b-primary': rangeIsWithinYear(startYear)}">
                        {{ startYear }}
                    </span>
                </button>
                <button
                    class="join-item range-btn font-medium grow range-preset-btn"
                    :class="{'range-preset-btn-active': selectedYear() === (startYear +1)}"
                    :value="startYear + 1"
                    @click="handleClick('YEAR', () => selectYear(startYear + 1))"
                >
                    <span :class="{'border-b-2 border-b-primary': rangeIsWithinYear(startYear + 1)}">
                        {{ startYear + 1 }}
                    </span>
                </button>
                <div class="join-item range-btn" @click="selectNextYear">
                    <i class="i-ph-caret-right"/>
                </div>
            </div>
        </div>
        <div v-if="!hideQuarterPicker" class="quarter-picker range-group">
            <button
                v-for="q in quarters"
                :key="q.quarter"
                data-row="QUARTER"
                :data-col="q.quarter"
                class="join-item range-btn grow range-preset-btn"
                :class="{'range-preset-btn-active' : q.selected}"
                @mousedown="handleMouseDown('QUARTER', q.quarter)"
                @mousemove="handleMouseMove"
                @mouseup="handleMouseUp"
                @click="handleClick('QUARTER', () => selectQuarter(q.quarter))"
            >
                Q{{ q.quarter }}
            </button>
        </div>
        <div v-if="!hideTermPicker" class="term-picker range-group">
            <div
                v-for="term in terms"
                :key="term.term"
                :title="`Termin ${term.term}`"
                class="join-item range-btn grow range-preset-btn"
                :class="{'range-preset-btn-active': term.selected}"
                data-row="TERM"
                :data-col="term.term"
                @mousedown="handleMouseDown('TERM', term.term)"
                @mousemove="handleMouseMove"
                @mouseup="handleMouseUp"
                @click="handleClick('TERM', () => selectTerm(term.term))"
            >
                T{{ term.term }}
            </div>
        </div>
        <div v-if="!hideMonthPicker" class="month-picker range-group">
            <div
                v-for="month in months"
                :key="month.month"
                data-row="MONTH"
                :data-col="month.month"
                class="join-item range-btn range-preset-btn"
                :class="{'range-preset-btn-active': month.selected }"
                style="text-transform: lowercase;"
                @click="handleClick('MONTH', () => selectMonth(month.month))"
                @mousedown="handleMouseDown('MONTH', month.month)"
                @mousemove="handleMouseMove"
                @mouseup="handleMouseUp"
            >
                {{ month.name }}
            </div>
        </div>
        <div class="flex mt-4 gap-1">
            <button
                v-if="!hideYtdPicker"
                class="range-btn range-preset-btn"
                :class="{'range-preset-btn-active': isYtdSelected}"
                @click="selectYtd"
            >
                YTD
            </button>
            <button class="range-btn range-preset-btn" :class="{'range-preset-btn-active': isTodaySelected}" @click="selectToday">DD</button>
        </div>
        <div v-if="!hideDatePicker" class="mt-4">
            {{ customPeriodLabel }}
        </div>
        <div v-if="!hideDatePicker">
            <div
                v-if="showStartDatePicker"
                class="join border border-base-300 hover:border-base-300 relative mr-2"
                @mouseover="onStartDateMouseOver"
                @mouseleave="onStartDateMouseLeave"
            >
                <tooltip
                    placement="top"
                    error-tooltip
                    needs-teleporting
                    :show-tip="showStartDateToolTip"
                >
                    <template #tooltipContent>
                        <div class="flex items-center gap-2">
                            <i class="i-ph-warning-fill text-error text-lg"/>
                            <div>{{ cachedT('create-transaction.validation.errors.START_DATE_LARGER_THAN_END_DATE') }}</div>
                        </div>
                    </template>

                    <template #content>
                        <DatePicker
                            ref="dateSelector"
                            v-model="startDate"
                            size="sm"
                            data-cy="from-date"
                            label="Fra"
                            :is-error="datepickerIsError"
                        />
                    </template>
                </tooltip>
            </div>
            <div
                class="join border border-base-300 hover:border-base-300  relative"
                @mouseover="onEndDateMouseOver"
                @mouseleave="onEndDateMouseLeave"
            >
                <tooltip
                    placement="top"
                    error-tooltip
                    needs-teleporting
                    :show-tip="showEndDateToolTip"
                >
                    <template #tooltipContent>
                        <div class="flex items-center gap-2">
                            <i class="i-ph-warning-fill text-error text-lg"/>
                            <div>{{ cachedT('create-transaction.validation.errors.END_DATE_LESS_THAN_START_DATE') }}</div>
                        </div>
                    </template>

                    <template #content>
                        <DatePicker
                            ref="dateSelector"
                            v-model="endDate"
                            data-cy="to-date"
                            size="sm"
                            label="Til"
                            :is-error="datepickerIsError"
                        />
                    </template>
                </tooltip>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>

import {DateRange, DateRangeCoordinates} from './DateRange'

import {computed, ref, watch} from 'vue'

import useDateRange from './useDateRange'

import DatePicker from '@/dateUtils/DatePicker.vue'
import Tooltip from '@/components/Tooltip.vue'

import {isDateBeforeOrEqual} from '@/services/formatter.service'
import {isBefore} from 'date-fns'
import {useCachedT} from '@/services/useCachedI18n'
import hotkeys from 'hotkeys-js'

const {cachedT} = useCachedT()

const props = defineProps({
    modelValue: {
        type: Object as () => DateRange,
        required: true
    },
    hideYtdPicker: {
        type: Boolean,
        default: false
    },
    hideYearPicker: {
        type: Boolean,
        default: false
    },
    hideQuarterPicker: {
        type: Boolean,
        default: false
    },
    hideTermPicker: {
        type: Boolean,
        default: false
    },
    hideMonthPicker: {
        type: Boolean,
        default: false
    },
    showStartDatePicker: {
        type: Boolean,
        default: true
    },
    customPeriodLabel: {
        type: String,
        default: 'Egendefinert periode'
    },
    hideDatePicker: {
        type: Boolean,
        default: false
    }
})

const {
    startDate, endDate,
    selectToday, isTodaySelected, selectYtd, isYtdSelected, rangeIsWithinYear,
    startYear, selectYear, selectPrevYear, selectNextYear, selectedYear,
    quarters, selectQuarter, selectQuarters,
    terms, selectTerm, selectTerms,
    months, selectMonth, selectMonths,
    selectPreviousPeriod, selectNextPeriod
} = useDateRange(props.modelValue?.start, props.modelValue?.end)

const emit = defineEmits(['update:modelValue'])

const emitUpdate = () => {
    emit('update:modelValue', {
        start: startDate.value,
        end: endDate.value
    })
}

const showStartDateToolTip = ref(false)
const showEndDateToolTip = ref(false)

const dateRangeType = ref<DateRangeCoordinates['row']>()

const datepickerIsError = computed(() => {
    if (!props.showStartDatePicker) return false
    return startDate > endDate
})
const onStartDateMouseOver = () => {
    if (datepickerIsError.value && !isDateBeforeOrEqual(startDate.value, endDate.value)) showStartDateToolTip.value = true
}
const onStartDateMouseLeave = () => {
    if (datepickerIsError.value && !isDateBeforeOrEqual(startDate.value, endDate.value)) showStartDateToolTip.value = false
}

const onEndDateMouseOver = () => {
    if (datepickerIsError.value && isBefore(endDate.value, startDate.value)) showEndDateToolTip.value = true
}
const onEndDateMouseLeave = () => {
    if (datepickerIsError.value && isBefore(endDate.value, startDate.value)) showEndDateToolTip.value = false
}

hotkeys('ctrl+shift+left', () => selectPreviousPeriod(dateRangeType.value))
hotkeys('ctrl+shift+right', () => selectNextPeriod(dateRangeType.value))

watch([startDate, endDate], emitUpdate)

const handleClick = (type: DateRangeCoordinates['row'], callBack: Function)  => {
    dateRangeType.value = type
    callBack()
}

const isDragging = ref(false)
const startCell = ref<DateRangeCoordinates>()
const endCell = ref<DateRangeCoordinates>()

const updateSelection = () => {
    if (!startCell.value || !endCell.value) return

    switch (startCell.value.row) {
        case 'QUARTER':
            selectQuarters({startQuarter: startCell.value?.col, endQuarter: endCell.value?.col})
            break
        case 'TERM':
            selectTerms({startTerm: startCell.value?.col, endTerm: endCell.value?.col})
            break
        case 'MONTH':
            selectMonths({startMonth: startCell.value?.col, endMonth: endCell.value?.col})
            break
        default:
            return
    }
}

const handleMouseDown = (row: DateRangeCoordinates['row'], col: number) => {
    isDragging.value = true
    startCell.value = {row, col}
    endCell.value = {row, col}
}

const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging.value || !startCell.value) return

    const targetCellElement = e.target as HTMLElement
    const currentHoverRow = targetCellElement.dataset.row as DateRangeCoordinates['row']
    const currentHoverCol = parseInt(targetCellElement.dataset.col || '-1')

    if (currentHoverCol === endCell.value?.col) return

    if (!currentHoverRow || currentHoverCol === -1) return

    endCell.value = {row: currentHoverRow, col: currentHoverCol}

    updateSelection()
}

const handleMouseUp = () => {
    isDragging.value = false
    startCell.value = undefined
    endCell.value = undefined
}

</script>

<style scoped>
@reference "@/index.css";
@reference "tailwindcss";

.range-group {
    @apply flex gap-0.5 join;
}

.range-btn {
    @apply btn btn-xs text-xs font-medium;
}

.range-preset-btn {
    @apply hover:bg-base-300 hover:text-white hover:bg-primary/60;
    &:hover {
        @apply border-primary;
    }

    &.range-preset-btn-active {
        @apply text-primary-content bg-primary hover:bg-primary;
    }
}

</style>